import React, { useEffect, useRef } from 'react';
import Modal from '@omilia/modal';
import { reduxForm, InjectedFormProps, SubmissionError } from 'redux-form';
import { compose } from 'redux';
import { connect } from 'react-redux';

// @ts-ignore
import { fetchEnvironmentsList } from 'store/services/environments/actions';
import { getEnvironmentsList } from 'store/services/environments/selectors';
import { EnvironmentItem } from 'scenes/ProjectSettings/scenes/Environments/EnvironmentsList/Environment/types';
import FORM_NAMES from 'constants/formNames';
import WizardForUploadAndDeploy from './WizardForUploadAndDeploy';
import useUploadAndDeployProject from './hooks/useUploadAndDeployModal';
import validate from '../validate';
import { UploadProjectModalFormValues } from '../types';
import { authenticateRequest } from '../api';

interface UploadProjectModalProps
  extends InjectedFormProps<UploadProjectModalFormValues> {
  willDeploy: boolean;
  onClose: () => void;
  fetchEnvironmentsList: () => void;
  environmentsList: EnvironmentItem[];
}

const UploadAndDeployProjectModal = ({
  onClose,
  pristine,
  invalid,
  fetchEnvironmentsList,
  environmentsList,
  handleSubmit,
  change,
}: UploadProjectModalProps) => {
  const {
    isUploadingAndDeploying,
    isLoadingClusters,
    clusters,
    authenticate,
    fetchClusters,
    setAuthenticatingStatus,
    setUploadAndDeployData,
    uploadAndDeployProject,
    uploadAndDeployError,
    resetModal,
  } = useUploadAndDeployProject();
  useEffect(() => {
    fetchEnvironmentsList();
  }, [fetchEnvironmentsList]);

  const selectRef = useRef<HTMLSelectElement>(null);

  const handleUploadAndDeploy = async () => {
    const succedeed = await uploadAndDeployProject(selectRef.current!.value);

    if (succedeed) {
      onClose();
    }
  };

  const handleContinueToSelectClusters = async ({
    applicationName,
    deploymentEnvironment,
    password,
    description,
  }: UploadProjectModalFormValues) => {
    setAuthenticatingStatus(true);
    try {
      await authenticateRequest(deploymentEnvironment, password);
      await fetchClusters({
        environmentId: deploymentEnvironment,
        password,
        description,
      });
      setUploadAndDeployData({
        environmentId: deploymentEnvironment,
        password,
        app_name: applicationName,
        description,
      });
    } catch (error) {
      setAuthenticatingStatus(false);
      throw new SubmissionError({
        password: 'Your password is invalid. Please try again.',
      });
    }
  };

  const {
    primaryAction,
    secondaryActions,
    title,
    content,
    glyph,
    variant,
    onBackClick,
  } = WizardForUploadAndDeploy({
    selectRef,
    pristine,
    invalid,
    onClose,
    continueToSelectClusters: handleSubmit(handleContinueToSelectClusters),
    handleUploadAndDeploy,
    handleBackClick: resetModal,
    environmentsList,
    isLoadingClusters,
    isUploadingAndDeploying,
    uploadAndDeployError,
    clusters,
    authenticate,
    change,
  });

  return (
    <Modal
      open
      shouldCloseOnBackdropClick
      glyph={glyph}
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
      title={title}
      variant={variant}
      onBackClick={onBackClick}
      onClose={onClose}
    >
      {content}
    </Modal>
  );
};

// @ts-ignore
const mapStateToProps = state => {
  const environmentsList = getEnvironmentsList(state);
  const deploymentEnvironmentInitialValue =
    environmentsList.length > 0 ? environmentsList[0].id : '';

  return {
    initialValues: {
      applicationName: environmentsList[0]?.app_name || '',
      deploymentEnvironment: deploymentEnvironmentInitialValue,
    },
    validate,
    environmentsList: getEnvironmentsList(state),
  };
};

const mapDispatchToProps = {
  fetchEnvironmentsList,
};

export default compose<UploadProjectModalProps>(
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm<UploadProjectModalFormValues, UploadProjectModalProps>({
    form: FORM_NAMES.DEPLOY.UPLOAD_AND_DEPLOY,
    enableReinitialize: true,
    validate,
    touchOnBlur: true,
    touchOnChange: true,
    destroyOnUnmount: true,
  }),
)(UploadAndDeployProjectModal);
